import { Fragment, useCallback, useEffect, useState } from "react";
import {
  fetchFoodWalkingIsochroneGenerationRuns,
  fetchFoodWalkingAccessProviderStatus,
  postFoodWalkingAccessCalculate,
  postFoodWalkingIsochronesDryRun,
  postFoodWalkingIsochronesRun,
} from "../../../../api/foodApi";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import { useAuth } from "../../../../auth/useAuth";

const FoodWalkingAccessSection = ({ canManage = false }) => {
  const { hasPermission } = useAuth();
  const canRun = canManage || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    try {
      const [providerStatus, runHistory] = await Promise.all([
        fetchFoodWalkingAccessProviderStatus(),
        fetchFoodWalkingIsochroneGenerationRuns({ limit: 5 }),
      ]);
      setStatus(providerStatus);
      setRuns(runHistory.runs || []);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.message || "Unable to load walking access status.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runIsochroneDryRun = async () => {
    if (!canRun || busy) return;
    try {
      setBusy(true);
      setResult(await postFoodWalkingIsochronesDryRun({ city: "Medellín", department: "Antioquia" }));
    } catch (error) {
      setErrorMessage(error.message || "Dry run failed.");
    } finally {
      setBusy(false);
    }
  };

  const runIsochroneApply = async () => {
    if (!canRun || busy) return;
    if (!window.confirm("Generate walking isochrones using the configured provider?")) return;
    try {
      setBusy(true);
      setResult(
        await postFoodWalkingIsochronesRun({
          city: "Medellín",
          department: "Antioquia",
          dryRun: false,
          confirm: true,
          provider: "mock",
        })
      );
      await load();
    } catch (error) {
      setErrorMessage(error.message || "Isochrone generation failed.");
    } finally {
      setBusy(false);
    }
  };

  const runWalkingAccessDryRun = async () => {
    if (!canRun || busy) return;
    try {
      setBusy(true);
      setResult(
        await postFoodWalkingAccessCalculate({
          city: "Medellín",
          department: "Antioquia",
          areaType: "NEIGHBORHOOD",
          dryRun: true,
        })
      );
    } catch (error) {
      setErrorMessage(error.message || "Walking access dry run failed.");
    } finally {
      setBusy(false);
    }
  };

  const runWalkingAccessApply = async () => {
    if (!canRun || busy) return;
    if (!window.confirm("Persist walking access metrics for access areas?")) return;
    try {
      setBusy(true);
      setResult(
        await postFoodWalkingAccessCalculate({
          city: "Medellín",
          department: "Antioquia",
          areaType: "NEIGHBORHOOD",
          dryRun: false,
          confirm: true,
        })
      );
    } catch (error) {
      setErrorMessage(error.message || "Walking access calculation failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel-card food-walking-access-section">
      <h2>Walking access</h2>
      <p className="muted">
        Estimated walking access zones are separate from straight-line 500 m buffers and existing
        access scores. External providers require explicit configuration and confirmation.
      </p>
      {errorMessage ? <p className="form-error">{errorMessage}</p> : null}
      {status ? (
        <div className="food-walking-access-section__kpi-row">
          <div>
            <span className="muted">Enabled</span>
            <strong>{status.config?.enabled ? "yes" : "no"}</strong>
          </div>
          <div>
            <span className="muted">Provider</span>
            <strong>{status.config?.provider || "mock"}</strong>
          </div>
          <div>
            <span className="muted">Stores with isochrones</span>
            <strong>{status.coverage?.storesWithIsochrones ?? 0}</strong>
          </div>
          <div>
            <span className="muted">Missing isochrones</span>
            <strong>{status.coverage?.storesMissingIsochrones ?? 0}</strong>
          </div>
        </div>
      ) : null}
      {canRun ? (
        <div className="food-walking-access-section__actions">
          <button type="button" className="btn btn--secondary" disabled={busy} onClick={runIsochroneDryRun}>
            Isochrone dry run
          </button>
          <button type="button" className="btn btn--primary" disabled={busy} onClick={runIsochroneApply}>
            Generate isochrones (mock)
          </button>
          <button type="button" className="btn btn--secondary" disabled={busy} onClick={runWalkingAccessDryRun}>
            Walking access dry run
          </button>
          <button type="button" className="btn btn--primary" disabled={busy} onClick={runWalkingAccessApply}>
            Calculate walking access
          </button>
        </div>
      ) : (
        <p className="muted">Admin actions require source manage permission.</p>
      )}
      {runs.length ? (
        <Fragment>
          <h3>Recent isochrone generation runs</h3>
          <ul>
            {runs.map((run) => (
              <li key={run.id}>
                {run.started_at} · {run.provider} · {run.status} · generated {run.generated_count}
              </li>
            ))}
          </ul>
        </Fragment>
      ) : null}
      {result ? (
        <pre className="food-walking-access-section__result">{JSON.stringify(result, null, 2)}</pre>
      ) : null}
    </section>
  );
};

export default FoodWalkingAccessSection;
